import React from 'react';
import { Badge, Button, Icon, DateDisplay } from '@aidkitorg/component-library';
import { SupportCasePreviewProps } from '../types';
import { getContactMethodIcon, getDisplayName } from '../utils';
import { useLocalizedStrings } from '../../../Localization';

/**
 * SupportCasePreview - Displays a single support case in a list-like format
 */
export const SupportCasePreview: React.FC<SupportCasePreviewProps> = ({
  request,
  messageCount,
  stage,
  onClaim,
  onClick
}) => {
  const { row, applicantInfos } = request;
  const L = useLocalizedStrings();
  const displayName = getDisplayName(request, L.admin_panel.support.unknown_contact);
  const contactIcon = getContactMethodIcon(row.contact_methods);

  // Check if we have applicant info (name is different from source)
  const hasApplicantName = Object.values(applicantInfos).length > 0 &&
    Object.values(applicantInfos).some(info =>
      info?.legal_name || info?.name || info?.first_name
    );

  // Use call transcript if contact method is 'call', otherwise use message text
  const isCallContact = row.contact_methods?.includes('call');
  const messagePreview = isCallContact && row.last_call_transcript
    ? row.last_call_transcript
    : row.last_message_text || '';

  const tags = row.tags || [];
  const isClaimed = row.assigned_to && row.assigned_to !== 'system';

  return (
    <div
      className="flex items-start gap-1 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        {/* Row 1: Contact icon + Name + message count + contact info */}
        <div className="flex items-center gap-2 mb-1">
          <Icon
            name={contactIcon}
            size="sm"
            className="text-gray-600"
            role="decorative"
          />
          <span className="font-semibold text-gray-900">
            {displayName}
          </span>
          {messageCount && messageCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
              {messageCount}
            </span>
          )}
          {hasApplicantName && (
            <span className="text-sm text-gray-500">
              {row.source}
            </span>
          )}
        </div>

        {/* Row 2: Message preview */}
        <div className="text-sm text-gray-700 mb-2">
          {messagePreview && (messagePreview.length > 200 ? messagePreview.slice(0, 200).trim() + '...' : messagePreview)}
        </div>

        {/* Row 3: Status + Stage (plain text) + Tags (pills) */}
        <div className="flex items-center gap-1.5">
          {row.support_case_status && row.support_case_status !== 'viewed' && (
            <span className="text-xs text-gray-600">
              {row.support_case_status.charAt(0).toUpperCase() + row.support_case_status.slice(1)}
            </span>
          )}
          {row.support_case_status && row.support_case_status !== 'viewed' && (
            <span className="text-xs text-gray-400">•</span>
          )}
          <span className="text-xs text-gray-600">
            {stage}
          </span>
          {tags.length > 0 && (
            <span className="text-lg text-gray-400">•</span>
          )}
          {tags.map((tag, index) => (
            <Badge key={index} style="pill" variant="info" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex flex-col justify-between items-end flex-shrink-0 self-stretch">
        <div className="text-xs text-gray-500 text-right">
          {row.max && (
            <DateDisplay
              utcValue={row.max}
              options={{
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }}
            />
          )}
        </div>
        {!isClaimed && (
          <Button
            size="sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              onClaim();
            }}
            className='mt-2'
          >
            {L.admin_panel.support.claim}
          </Button>
        )}
        {isClaimed && row.assigned_to_name && (
          <div className="text-xs text-gray-600 text-right">
            <div className="font-medium">{row.assigned_to_name}</div>
          </div>
        )}
      </div>
    </div>
  );
};
